import type { BackorderRow } from '@eombor/contracts';

/**
 * BILDIRGILARNI GURUHLASH — ikki xil savolga ikki xil javob.
 *
 * Skladchining navbatida o'nlab ariza yig'iladi va ular yakka-yakka
 * turganda ikkita savol javobsiz qoladi:
 *
 *   - `product` — «bu mahsulotdan jami qancha kerak?» Ta'minotga
 *     buyurtma aynan shu son bilan beriladi: besh kishi beshta alohida
 *     ariza yozgan bo'lsa ham, buyurtma bitta bo'lishi kerak.
 *   - `batch`  — «bu uchtasi bitta ish uchunmi?» Xodim savatga bir necha
 *     mahsulot solib bitta tugma bosadi, lekin har biri alohida bildirgi
 *     bo'lib yashaydi va navbatda begona arizalar orasiga sochilib
 *     ketadi.
 *
 * Funksiya SOF: tartib kirishdagidek saqlanadi (server `createdAt`
 * bo'yicha beradi) va hech bir qator yo'qolmaydi — guruhlanmaydigan
 * qator o'z yakka guruhini oladi.
 */

export type GroupMode = 'none' | 'product' | 'batch';

export interface BackorderGroup {
  key: string;
  /** Guruh sarlavhasi — `none` da bo'sh. */
  title: string;
  /** Guruh ostidagi qator — `none` da har guruhda bittadan. */
  rows: BackorderRow[];
  /** Jami miqdor — faqat birlik BIR XIL bo'lganda ma'noli. */
  totalQty: number;
  unit: string | null;
}

/** Mahsulot kaliti: katalogga bog'langanida `id`, aks holda erkin matn. */
const productKeyOf = (row: BackorderRow): string =>
  row.nomenclatureId === null ? `free:${(row.freeText ?? '').trim().toLowerCase()}` : `nom:${row.nomenclatureId}`;

const productTitleOf = (row: BackorderRow): string => row.nomenclatureName ?? row.freeText ?? '';

/**
 * To'plam sarlavhasi — KIM va NIMA UCHUN.
 *
 * Hujjat raqami emas: to'plamning o'z raqami yo'q va bo'lishi ham
 * shart emas — skladchi uni «Toshevning yoritgich ishi» deb eslaydi.
 */
const batchTitleOf = (row: BackorderRow): string => `${row.requesterName} · ${row.purpose}`;

/** Miqdorni uch xonagacha yaxlitlaydi — ustun `numeric(14,3)`. */
const round3 = (value: number): number => Math.round(value * 1000) / 1000;

/**
 * Guruhning BIRLIGI — hamma qatorda bir xil bo'lsagina.
 *
 * Aralash birlikda (metr va dona) yig'indining ma'nosi yo'q va uni
 * ko'rsatish yolg'on bo'lardi.
 */
const unitOf = (rows: readonly BackorderRow[]): string | null => {
  const units = new Set(rows.map((row) => row.unit ?? ''));
  if (units.size !== 1) return null;
  const only = [...units][0] ?? '';
  return only === '' ? null : only;
};

export const groupBackorders = (
  rows: readonly BackorderRow[],
  mode: GroupMode,
): BackorderGroup[] => {
  if (mode === 'none') {
    return rows.map((row) => ({
      key: row.id,
      title: '',
      rows: [row],
      totalQty: round3(row.requestedQty),
      unit: row.unit,
    }));
  }

  const order: string[] = [];
  const buckets = new Map<string, BackorderRow[]>();

  for (const row of rows) {
    /* To'plamsiz ariza (yakka yuborilgan yoki ustun qo'shilishidan
       oldingisi) O'Z guruhini oladi — u yo'qolib qolmasligi kerak. */
    const key =
      mode === 'product'
        ? productKeyOf(row)
        : row.batchId === null
          ? `single:${row.id}`
          : `batch:${row.batchId}`;

    const bucket = buckets.get(key);
    if (bucket === undefined) {
      buckets.set(key, [row]);
      order.push(key);
      continue;
    }
    bucket.push(row);
  }

  return order.map((key) => {
    const bucket = buckets.get(key) ?? [];
    const first = bucket[0];
    return {
      key,
      title:
        first === undefined
          ? ''
          : mode === 'product'
            ? productTitleOf(first)
            : batchTitleOf(first),
      rows: bucket,
      totalQty: round3(bucket.reduce((sum, row) => sum + row.requestedQty, 0)),
      unit: unitOf(bucket),
    };
  });
};
